<template>
  <div class="call-center-orders-page">
    <template>
      <order-filters
        :statuses="callCentersTabs"
        :value="currentTab"
        :disabled="!!ordersSearchString"
        @filterOrders="changeTab"
      />

      <div class="search-container">
        <div class="search-inner">
          <orders-full-width-search-input
            v-if="isCallCenter"
          />
        </div>
      </div>

      <h1
        v-if="!!ordersSearchString"
        class="display-3 font-weight-medium mb-5"
      >
        Результаты поиска:
        <!--        &laquo;-->
        <search-type-icon :search-string="ordersSearchString" class="orders-search-icon" />
        {{ ordersSearchString }}
        <!--        &raquo;-->
      </h1>
      <h1 v-else-if="currentTabTitle" class="display-3 font-weight-medium mb-5">
        {{ currentTabTitle }}
      </h1>

      <div class="orders-listing-area">
        <transition name="fadeUp">
          <v-loader v-if="isLoading && showBigLoader" absolute transparent />
        </transition>

        <transition
          name="fadeIn"
          :duration="{ enter: 300, leave: 0 }"
        >
          <div v-if="!showBigLoader && orders.length">
            <div
              v-for="order in orders"
              :key="order.id"
            >
              <order-card
                :order="order"
                class="order-card"
                @changeStatus="changeOrderStatus"
                @setAssemblyStatus="setAssemblyStatus"
                @setReadyStatus="setReadyStatus"
              />
            </div>
          </div>
        </transition>

        <template v-if="!isLoading && orders.length === 0">
          <span class="display-4 text-center d-block mt-16">
            <template v-if="!!ordersSearchString">
              Ничего не найдено
            </template>
            <template v-else-if="ordersFilterParam">
              Нет заказов
            </template>
          </span>
        </template>
      </div>
    </template>

    <transition
      name="fadeUp"
      :duration="80"
    >
      <v-card
        v-if="isLoading && !showBigLoader"
        class="loading-card"
        elevation="24"
      >
        <v-loader />
      </v-card>
    </transition>
    <payment-dialog />
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import moment from 'moment'
  import VLoader from '@/components/loader/Loader'
  import OrderFilters from '@/components/order-filters/OrderFilters'
  import OrderCard from '@/components/order-card/OrderCard'
  import mixinSnackbarMessage from '@/mixins/snackbar-message'
  import _ from 'lodash'
  import axios from 'axios'
  import PaymentDialog from '@/components/order-processing/order-components/payment/PaymentDialog.vue'
  import PaymentMode from '@/models/enums/PaymentMode'
  import CRMPermission from '@/models/enums/permissions/CRMPermission'
  import PaymentStatus from '@/models/enums/PaymentStatus'
  import SearchTypeIcon from '@/components/icons/SearchTypeIcon.vue'
  import SearchInput from '@/components/header/SearchInput.vue'
  import OrdersFullWidthSearchInput from '@/components/orders-list/OrdersFullWidthSearchInput.vue'
  import {
    DEFAULT_CALL_CENTER_TAB,
    buildOrdersQuery,
    getFilterFromRoute,
    pushOrdersRoute,
  } from '@/util/orders-route'
  import { websocketService } from '@/services/WebsocketService'
  import { refreshService } from '@/services/refresh'
  import { UPDATE_ORDER } from '@/constants/websocket-events'

  const CALL_CENTER_TABS = [
    {
      count: -1,
      filterParam: 'main',
      ruName: 'Дашборд',
      hasOrders: false,
    },
    {
      count: -1,
      filterParam: 'lateness',
      ruName: 'Опоздания',
      ordersFilterParam: 'lateness',
      hasOrders: true,
      limit: 30,
      title: 'ТОП-30 опозданий',
    },
    {
      count: -1,
      filterParam: 'canceled',
      ruName: 'Отмененные',
      hasOrders: false,
      title: 'В разработке...',
    },
    {
      count: -1,
      filterParam: 'filial-stops',
      ruName: 'Стопы филиалов',
      hasOrders: false,
      title: 'В разработке...',
    },
  ]

  const CALL_CENTER_TAB_VALUES = CALL_CENTER_TABS.map(tab => tab.filterParam)

  export default {
    name: 'CallCenterOrdersDashboard',
    components: {
      OrdersFullWidthSearchInput,
      SearchTypeIcon,
      VLoader,
      OrderFilters,
      OrderCard,
      PaymentDialog,
    },
    mixins: [mixinSnackbarMessage],
    inject: ['announceMissedNewOrders'],
    data () {
      return {
        // Канал, на который подписались: websocketOrdersChanelName пересчитывается
        // при смене филиала, отписываться надо от старого
        ordersChannel: null,
        isLoading: false,
        showBigLoader: false,
        // currentOrderStatus: null,
        currentTab: getFilterFromRoute(this.$route, CALL_CENTER_TAB_VALUES, DEFAULT_CALL_CENTER_TAB),
        showSearchTitle: false,
        statuses: [],
        defaultLimit: 500,
        callCentersTabs: CALL_CENTER_TABS.map(tab => ({ ...tab })),
      }
    },
    computed: {
      ...mapGetters({
        localStorageBranch: 'branches/getLocalStorageBranch',
        isCallCenter: 'branches/getGlobalFilialIsCallCenter',
      }),
      ...mapState({
        ordersSearchString: state => state.orders.ordersSearchString,
        ordersFilterDate: state => state.orders.ordersFilterDate,
        orders: state => state.orders.orders,
        currentOrderInfo: state => state.orders.currentOrderInfo,
        showPaymentDialog: state => state.orders.showPaymentDialog,
        user: state => state.auth.user || {},
        showFloatingButtons: (state) => state.app.showFloatingButtons,
        paymentMode: state => state.newOrder.paymentMode,
      }),

      authUserId () { return this.user.id || 0 },
      // диалог оплаты после оформления: только реальный неоплаченный заказ - режим «оплатить
      // сейчас» мог залипнуть в сторе от брошенного оформления, а currentOrderInfo быть чужим
      canAutoOpenPaymentDialog () {
        return this.userCan(CRMPermission.MakeOrderPayment) &&
          this.paymentMode === PaymentMode.PayNow &&
          !this.showPaymentDialog &&
          !!this.currentOrderInfo?.id &&
          this.currentOrderInfo?.payment?.status !== PaymentStatus.Success
      },
      websocketOrdersChanelName () {
        if (this.isCallCenter) {
          return 'orders.filial.call_center'
        }

        return `orders.filial.${this.localStorageBranch.id}`
      },
      currentTabInfo () {
        return this.callCentersTabs.find(tab => tab.filterParam.toLowerCase() === this.currentTab.toLowerCase())
      },
      currentTabTitle () {
        return this.currentTabInfo?.title
      },
      ordersFilterParam () {
        return this.currentTabInfo?.ordersFilterParam
      },

      ordersLimit () {
        return this.currentTabInfo?.limit || this.defaultLimit
      },
    },
    watch: {
      '$route.query.filter' () {
        const next = getFilterFromRoute(this.$route, CALL_CENTER_TAB_VALUES, DEFAULT_CALL_CENTER_TAB)
        if (next === this.currentTab) {
          return
        }
        this.currentTab = next
        if (this.ordersSearchString) {
          return
        }
        this.getOrders(true)
      },
      ordersFilterDate () {
        this.getOrders()
        this.getFilters()
      },
      ordersSearchString () {
        if (this.ordersSearchString) {
          this.searchOrders()
        } else {
          this.getOrders(true)
        }
      },
      currentOrderInfo () {
        if (this.canAutoOpenPaymentDialog) {
          this.$store.commit('orders/setCurrentEditOrder', this.currentOrderInfo)
          this.$store.commit('orders/setShowPaymentDialog', true)
        }
      },
      isLoading (val) {
        if (!val) {
          this.showBigLoader = false
        }
      },
      statuses (data) {
        if (_.isArray(data)) {
          data.forEach(status => {
            this.callCentersTabs = this.callCentersTabs.map(tab => {
              if (tab.ordersFilterParam?.toLowerCase() === status.filterParam.toLowerCase()) {
                const limit = tab.limit || this.defaultLimit
                tab.count = status.count > limit ? limit + '+' : status.count
              }
              return tab
            })
          })
        }
      },
    },
    created () {
      this.$store.commit('orders/setOrdersList', [])

      this.$nextTick(() => {
        if (this.canAutoOpenPaymentDialog) {
          this.$store.commit('orders/setCurrentEditOrder', this.currentOrderInfo)
          this.$store.commit('orders/setShowPaymentDialog', true)
        }

        if (this.ordersSearchString) {
          this.searchOrders()
        } else {
          this.getOrders(true)
        }
        this.getFilters()

        /* eslint-disable */
        const processCallEvent = () => {
          if (this.ordersSearchString) return
          //this.getOrders()
          this.getFilters()
        }

        this.$store.dispatch('auth/fetchUser')
          .then(() => {
            // Сокет общий на вкладку: подписка ушедшей страницы дёргала бы
            // getOrders уничтоженного компонента
            if (this._isDestroyed) return
            this.ordersChannel = this.websocketOrdersChanelName
            websocketService.listenEvent(this.ordersChannel, UPDATE_ORDER, this.processWebsocketEvent)
          })
        refreshService.register(this.refreshOrders, { probe: this.ordersProbe })
      })
    },
    beforeDestroy () {
      this.$store.commit('orders/setOrdersSearchString', null)
      // Только со своим колбэком: App.vue слушает тот же UpdateOrder на том же
      // канале ради звука нового заказа. Раньше отписка шла от orders.filial.{id},
      // а подписка колл-центра - на orders.filial.call_center
      if (this.ordersChannel) {
        websocketService.stopListening(this.ordersChannel, UPDATE_ORDER, this.processWebsocketEvent)
      }
      refreshService.unregister(this.refreshOrders)

      /*  echo.channel('orders.filial.all')
         .stopListening('Order\\UpdateOrder')
         // .stopListening('Order\\UpdateStatusesOrder')
       this.$store.commit('orders/clearOrdersList') */

    },
    methods: {
      changeTab (value) {
        pushOrdersRoute(this.$router, buildOrdersQuery({
          filter: value === DEFAULT_CALL_CENTER_TAB ? null : value,
          search: this.ordersSearchString,
        }))
      },
      setAssemblyStatus (id) {
        const data = {
          orderStatus: 'assembly',
        }
        this.$store.dispatch('orders/updateOrder', { id, data })
          .then(res => {
            if (res.status === 200) {
              this.getOrders()
            } else {
              this.showErrorMessage(res.response.data.message)
            }
          })
      },
      setReadyStatus (id) {
        const data = {
          orderStatus: 'readyForIssue',
        }
        this.$store.dispatch('orders/updateOrder', { id, data })
          .then(res => {
            if (res.status === 200) {
              this.getOrders()
            } else {
              this.showErrorMessage(res.response.data.message)
            }
          })
      },
      changeOrderStatus (id) {
        this.$store.dispatch('orders/changeOnNextStatus', id)
          .catch(e => this.showErrorFromResponse(e))
      },
      getOrders (showBigLoader = false, { silent = false } = {}) {
        if (!this.ordersFilterParam) {
          this.$store.commit('orders/setOrdersList', [])
          return
        }
        this.isLoading = true
        this.showBigLoader = showBigLoader
        const payload = {
          limit: this.ordersLimit,
          offset: 0,
          page: 1,
          filterParam: this.ordersFilterParam,
          date: this.ordersFilterDate,
        }
        if (this.localStorageBranch) {
          payload.filialId = this.localStorageBranch.id
        }
        return this.$store
          .dispatch('orders/fetchOrdersList', payload)
          .then((res) => {
            this.isLoading = false
            this.showSearchTitle = false
            this.rememberOrdersVersion(res)
          })
          .catch((err) => {
            if (!silent) {
              this.showErrorFromResponse(err)
            }
            if (!axios.isCancel(err)) {
              this.isLoading = false
              this.showSearchTitle = false
            }
          })
      },
      searchOrders () {
        this.isLoading = true
        this.showBigLoader = true
        const payload = {
          limit: 30,
          offset: 0,
          page: 1,
          date: moment(new Date(Date.now())).format('YYYY-MM-DD'),
          globalSearch: this.ordersSearchString,
        }
        this.$store
          .dispatch('orders/fetchOrdersList', payload)
          .then(() => {
            this.isLoading = false
            this.showSearchTitle = true
          })
          .catch((err) => {
            this.showErrorFromResponse(err)
            if (!axios.isCancel(err)) {
              this.isLoading = false
              this.showSearchTitle = false
            }
          })
      },
      getFilters ({ silent = false } = {}) {
        const payload = {
          date: this.ordersFilterDate,
          filialId: this.localStorageBranch.id,
        }
        return this.$store.dispatch('orders/fetchStatusesList', payload)
          .then(res => {
            this.statuses = res
          })
          .catch((err) => {
            if (!silent) {
              this.showErrorFromResponse(err)
            }
          })
      },
      // Параметры версии для сторожа: те же, что у getOrders. В режиме поиска на экране
      // результат поиска - версию списка не проверяем
      ordersProbe () {
        if (this.ordersSearchString || !this.localStorageBranch || !this.ordersFilterParam) return null
        return {
          filialId: this.localStorageBranch.id,
          date: this.ordersFilterDate,
          filterParam: this.ordersFilterParam,
        }
      },
      // Версия только что загруженного списка: сторож сравнит с ней и не станет
      // перечитывать то, что мы сами только что получили
      rememberOrdersVersion (res) {
        const meta = res && res.data && res.data.meta
        refreshService.setVersion(this.refreshOrders, (meta && meta.version) || null)
      },
      // Догоняем пропущенное после разрыва сокета и по сторожу. В режиме поиска
      // на экране результат поиска - его не перезаписываем, как processWebsocketEvent
      refreshOrders ({ silent }) {
        if (this.ordersSearchString) return
        const shownOrders = this.orders
        const shownFilter = this.ordersFilterParam
        const shownDate = this.ordersFilterDate
        return Promise.all([this.getOrders(false, { silent }), this.getFilters({ silent })])
          .then(() => {
            // Пока шёл запрос, сменили фильтр, дату или начали поиск - на экране другой
            // список, и новые в нём заказы не пропущенные
            const sameView = !this.ordersSearchString &&
              this.ordersFilterParam === shownFilter &&
              this.ordersFilterDate === shownDate
            if (!sameView) return
            this.announceMissedNewOrders(shownOrders, this.orders)
          })
      },
      processWebsocketEvent: _.debounce(function () {
        if (this.ordersSearchString) return
        this.getOrders()
        this.getFilters()
      }, 1000),
    },
  }
</script>

<style lang="less" scoped>
.call-center-orders-page {
  //padding: 15px 0 15px 4px;



  .orders-search-icon {
    color: @color-primary;
    margin-right: 3px;
    margin-left: 5px;
  }


  .orders-listing-area {
    min-height: 200px;
    position: relative;
  }
}
.order-card {
  margin-bottom: 10px;
}
.loading-card {
  z-index: 5;
  position: fixed;
  right: 24px;
  bottom: 24px;
  ::v-deep {
    .loader-component div {
      margin: 20px;
    }
  }
}

.search {
  &-container {

    display: flex;
    justify-content: flex-end;
  }

  &-inner {
    max-width: 100%;
    width: 1000px;
    //flex-shrink: 0;
    flex-grow: 1;
    //flex-basis: 50%;
  }
}
</style>

